/**
 * LivePreview — displays a live preview element styled with the current StyleProperties.
 * Single responsibility: render sample content reflecting the active style state.
 */

import React from 'react';
import type { StyleProperties } from './types';

export interface LivePreviewProps {
  slot: string;
  styles: StyleProperties;
  isDark: boolean;
}

/**
 * Convert a StyleProperties object to React.CSSProperties.
 * Numbers are treated as px values for dimensional properties.
 * String properties are passed through directly.
 */
function stylesToCSS(s: StyleProperties): React.CSSProperties {
  const PX_FIELDS = new Set<keyof StyleProperties>([
    'fontSize', 'letterSpacing',
    'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'marginTop', 'marginRight', 'marginBottom', 'marginLeft',
    'gap', 'rowGap', 'columnGap',
    'borderWidth', 'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
    'borderRadius', 'borderTopLeftRadius', 'borderTopRightRadius',
    'borderBottomRightRadius', 'borderBottomLeftRadius',
    'outlineWidth', 'outlineOffset',
  ]);

  const result: Record<string, unknown> = {};

  for (const [key, val] of Object.entries(s)) {
    if (val === undefined || val === null) continue;
    const k = key as keyof StyleProperties;
    if (typeof val === 'number' && PX_FIELDS.has(k)) {
      result[key] = `${val}px`;
    } else if (key === 'lineHeight' && typeof val === 'number') {
      result[key] = val; // unitless
    } else if (key === 'opacity' && typeof val === 'number') {
      result[key] = val; // 0-1
    } else if (key === 'zIndex' && typeof val === 'number') {
      result[key] = val; // integer
    } else {
      result[key] = val;
    }
  }

  return result as React.CSSProperties;
}

/**
 * LivePreview — renders sample content styled with the provided StyleProperties.
 */
export const LivePreview: React.FC<LivePreviewProps> = ({ slot, styles, isDark }) => {
  const wrapperBg = isDark ? 'rgba(2,6,23,0.8)' : '#f1f5f9';
  const labelColor = isDark ? '#64748b' : '#94a3b8';
  const defaultBg = isDark ? 'rgba(30,41,59,0.6)' : '#ffffff';
  const defaultBorder = isDark ? 'rgba(51,65,85,0.5)' : '#e2e8f0';
  const defaultText = isDark ? '#e2e8f0' : '#1e293b';

  const appliedStyle: React.CSSProperties = {
    background: defaultBg,
    border: `1px solid ${defaultBorder}`,
    color: defaultText,
    borderRadius: 6,
    padding: 12,
    fontSize: 13,
    minHeight: 48,
    ...stylesToCSS(styles),
  };

  const wrapperStyle: React.CSSProperties = {
    background: wrapperBg,
    borderRadius: 6,
    padding: 10,
    marginTop: 2,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: labelColor,
    marginBottom: 6,
  };

  const renderContent = () => {
    switch (slot) {
      case 'containerStyle':
        return (
          <div style={appliedStyle}>
            <div style={{ marginBottom: 8, fontWeight: 600, fontSize: 14 }}>Card Title</div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Sample card content goes here.</div>
          </div>
        );
      case 'headerStyle':
        return (
          <div style={appliedStyle}>
            Label text
          </div>
        );
      case 'bodyStyle':
        return (
          <div style={{ ...appliedStyle, display: 'flex', alignItems: 'center' }}>
            <div style={{
              flex: 1,
              height: 28,
              borderRadius: 4,
              background: isDark ? 'rgba(2,6,23,0.5)' : '#f8fafc',
              border: `1px solid ${defaultBorder}`,
              display: 'flex',
              alignItems: 'center',
              paddingLeft: 8,
              fontSize: 12,
              color: isDark ? '#64748b' : '#94a3b8',
            }}>
              Input field...
            </div>
          </div>
        );
      case 'contentStyle':
        return (
          <div style={appliedStyle}>
            The quick brown fox jumps over the lazy dog
          </div>
        );
      case 'buttonStyle':
        return (
          <div style={{ padding: 4 }}>
            <button
              type="button"
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: `1px solid ${defaultBorder}`,
                background: isDark ? '#22c55e' : '#16a34a',
                color: '#ffffff',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                ...stylesToCSS(styles),
              }}
              aria-label="Sample button preview"
            >
              Button
            </button>
          </div>
        );
      default:
        return <div style={appliedStyle}>Preview</div>;
    }
  };

  return (
    <div style={wrapperStyle}>
      <div style={labelStyle}>Preview</div>
      {renderContent()}
    </div>
  );
};

LivePreview.displayName = 'LivePreview';
